import { Button } from "./Button";
import { usePagePermission } from "@/hooks/permission/usePagePermission";
import { TableColumn } from "./Table";
import { useExcelDownload } from "@/hooks/common/useExcelDownload";
import { ExcelDownloadOptions } from "@/lib/excel";

interface ExcelButtonProps<T> extends ExcelDownloadOptions {
  /** 다운로드할 데이터 */
  data: T[];
  /** 테이블 컬럼 정의 */
  columns: TableColumn[];
  /** 버튼 텍스트 (기본: "엑셀") */
  children?: React.ReactNode;
  /** 권한 체크 비활성화 여부 */
  skipPermissionCheck?: boolean;
  /** 추가 비활성화 조건 */
  disabled?: boolean;
  /** 다운로드 전 콜백 */
  onBeforeDownload?: () => void;
  /** 다운로드 후 콜백 */
  onAfterDownload?: () => void;
  /** 에러 발생 시 콜백 */
  onError?: (error: Error) => void;
  /** 버튼 클래스 */
  className?: string;
}

/**
 * 엑셀 다운로드 버튼 컴포넌트
 * 권한 체크와 엑셀 다운로드 기능이 통합되어 있음
 *
 * @example
 * // 기본 사용
 * <ExcelButton
 *   data={tableData}
 *   columns={columns}
 *   fileName="매출현황"
 * />
 *
 * @example
 * // 옵션 포함
 * <ExcelButton
 *   data={tableData}
 *   columns={columns}
 *   fileName="거래내역"
 *   sheetName="상세내역"
 *   includeGroupHeader={true}
 *   onAfterDownload={() => toast.success("다운로드 완료")}
 * >
 *   엑셀 다운로드
 * </ExcelButton>
 */
export function ExcelButton<T extends Record<string, any>>({
  data,
  columns,
  children = "엑셀",
  skipPermissionCheck = false,
  disabled = false,
  fileName,
  sheetName,
  includeGroupHeader,
  includeDateInFileName,
  onBeforeDownload,
  onAfterDownload,
  onError,
  className,
}: ExcelButtonProps<T>) {
  const pagePermission = usePagePermission();

  const { download, isDownloading } = useExcelDownload<T>({
    fileName,
    sheetName,
    includeGroupHeader,
    includeDateInFileName,
    onBeforeDownload,
    onAfterDownload,
    onError,
  });

  // 권한 체크
  const hasPermission = skipPermissionCheck || pagePermission.canExport;
  const isDisabled =
    !hasPermission || pagePermission.isLoading || disabled || isDownloading || data.length === 0;

  const handleClick = () => {
    if (!isDisabled) {
      download(data, columns);
    }
  };

  return (
    <Button
      variant="etc"
      className={className}
      disabled={isDisabled}
      onClick={handleClick}
    >
      {isDownloading ? "다운로드 중..." : children}
    </Button>
  );
}

export default ExcelButton;
